import React, { useContext, useMemo, useState, useLayoutEffect, useEffect, useRef } from "react";
import { useReactTable, getCoreRowModel, getSortedRowModel, getPaginationRowModel, flexRender } from "@tanstack/react-table";
import { AuthContext } from "../contexts";
import axios from "axios";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { toastOptions } from "../assets/constants";
import emptyIcon from "../assets/images/empty-list.png";
import { CircularProgress } from "@mui/material";
import { CustomDownloader } from ".";
import CustomCheckbox from "./CustomCheckbox";

const CustomList = ({
    entity,
    columns,
    setSelectedColumns,
    setSelectedIds,
    refreshData,
    intermediatePath,
    activeFilters,
    setData,
    rowIdAccessor,
    isSelectable = true,
    isPaginable = true,
    isFetchable = true,
    isClickable = true,
    isDownloadable = false,
    isFixed = false,
    isScrollable = false,
    hiddenColumns = [],
    limit = 100,
    entityType,
    emptyPlaceholder = "Il n'y a encore aucun élément dans cette liste.",
    initialSortBy = [],
    onSortChange,
    initialPageIndex = 0,
    initialPageSize = null,
    onPaginationChange,
    onRowCLick,
}) => {
    const { token } = useContext(AuthContext);
    const [items, setItems] = useState([]);
    const [isLoaded, setIsLoaded] = useState(false);
    const data = useMemo(() => items, [items]);

    const scrollContainerRef = useRef(null);

    const handleWheel = (e) => {
        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer) {
            const sensitivity = 1.5;
            scrollContainer.scrollLeft += e.deltaY * sensitivity;
        }
    };

    useEffect(() => {
        let isMounted = true;
        const scrollContainer = scrollContainerRef.current;

        if (isMounted && scrollContainer) {
            scrollContainer.addEventListener("wheel", handleWheel, {
                passive: false,
            });
        }

        return () => {
            isMounted = false;
            if (scrollContainer) {
                scrollContainer.removeEventListener("wheel", handleWheel);
            }
        };
    }, []);

    useLayoutEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer) {
            scrollContainer.addEventListener("wheel", handleWheel, {
                passive: false,
            });
        }

        return () => {
            if (scrollContainer) {
                scrollContainer.removeEventListener("wheel", handleWheel);
            }
        };
    }, []);

    React.useEffect(() => {
        const applyFilters = (data) => {
            if (data) {
                let filteredData = [...data];
                activeFilters &&
                    activeFilters.forEach((column) => {
                        let columnData = [];
                        if (column.icon !== "calendar") {
                            column.selectedValues &&
                                column.selectedValues.forEach((value) => {
                                    switch (true) {
                                        case column.accessor === "project.name" || column.accessor === "customer.name" || column.accessor === "provider" || column.accessor === "sender":
                                            columnData = columnData.concat(data.filter((row) => row[column.accessor.split(".")[0]].name === value.value));
                                            break;
                                        case column.accessor === "project_managers.0":
                                            columnData = columnData.concat(data.filter((row) => row.project_managers[0].firstName + " " + row.project_managers[0].lastName === value.value));
                                            break;
                                        default:
                                            columnData = columnData.concat(data.filter((row) => row[column.accessor] === value.value));
                                            break;
                                    }
                                });
                        } else {
                            columnData = data.filter((row) => {
                                if (row[column.accessor]) {
                                    if (column.startDate && column.stopDate) {
                                        return dayjs(row[column.accessor]).isBetween(column.startDate, column.stopDate);
                                    } else if (column.startDate && !column.stopDate) {
                                        return dayjs(row[column.accessor]).isSameOrAfter(column.startDate);
                                    } else if (!column.startDate && column.stopDate) {
                                        return dayjs(row[column.accessor]).isSameOrBefore(column.stopDate);
                                    } else {
                                        return false;
                                    }
                                } else {
                                    return false;
                                }
                            });
                        }
                        if ((column.icon === "calendar" && (column.startDate || column.stopDate)) || columnData.length > 0) {
                            filteredData = filteredData.filter((row) => columnData.includes(row));
                        }
                    });
                setItems(filteredData);
            }
        };

        setIsLoaded(false);

        if (!isFetchable || typeof entity !== "string") {
            applyFilters(entity);
            setIsLoaded(true);
        } else {
            axios
                .get(`${process.env.REACT_APP_UPA_API_HOST}${entity}`, {
                    headers: { Authorization: `Bearer ${token}` },
                })
                .then((response) => {
                    if (typeof setData === "function") {
                        setData(response.data);
                    }
                    applyFilters(response.data);
                    setIsLoaded(true);
                })
                .catch((error) => {
                    console.error("AxiosError: ", error);
                    toast.error(error.message, toastOptions);
                    setIsLoaded(true);
                });
        }
    }, [entity, refreshData, token, setData, activeFilters, isFetchable]);

    const [rowSelection, setRowSelection] = useState({});
    const [sorting, setSorting] = useState(initialSortBy);
    const [pagination, setPagination] = useState({
        pageIndex: initialPageIndex,
        pageSize: initialPageSize || limit,
    });

    const tableColumns = useMemo(() => {
        if (!columns || !Array.isArray(columns)) {
            return [];
        }

        let cols = columns.map((col) => ({
            ...col,
            header: col.Header || col.header,
            cell: col.Cell
                ? (info) => {
                      const value = info.getValue();
                      const cellProps = {
                          row: {
                              original: info.row.original,
                              index: info.row.index,
                              values: info.row.original,
                          },
                          value: value,
                          column: {
                              id: info.column.id,
                          },
                          cell: {
                              value: value,
                          },
                      };

                      try {
                          return col.Cell(cellProps);
                      } catch (error) {
                          console.error("Error in cell renderer:", error, col);
                          return value || "";
                      }
                  }
                : col.cell,
            accessorKey: col.accessor || col.accessorKey,
        }));

        if (isSelectable) {
            cols = [
                {
                    id: "selection",
                    header: ({ table }) => (
                        <div>
                            <CustomCheckbox
                                checked={table.getIsAllPageRowsSelected()}
                                indeterminate={table.getIsSomePageRowsSelected()}
                                onChange={table.getToggleAllPageRowsSelectedHandler()}
                                ariaLabel="Tout sélectionner"
                            />
                        </div>
                    ),
                    cell: ({ row }) => (
                        <div>
                            <CustomCheckbox checked={row.getIsSelected()} disabled={!row.getCanSelect()} onChange={row.getToggleSelectedHandler()} ariaLabel="Sélectionner la ligne" />
                        </div>
                    ),
                },
                ...cols,
            ];
        }
        return cols;
    }, [columns, isSelectable]);

    const [columnVisibility, setColumnVisibility] = useState(() => {
        const visibility = {};
        if (hiddenColumns && hiddenColumns.length > 0) {
            hiddenColumns.forEach((colId) => {
                visibility[colId] = false;
            });
        }
        return visibility;
    });

    const table = useReactTable({
        data,
        columns: tableColumns,
        state: {
            rowSelection,
            sorting,
            pagination,
            columnVisibility,
        },
        onRowSelectionChange: setRowSelection,
        onSortingChange: setSorting,
        onPaginationChange: setPagination,
        onColumnVisibilityChange: setColumnVisibility,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: isPaginable ? getPaginationRowModel() : undefined,
        enableRowSelection: isSelectable,
        enableSorting: true,
        enableMultiSort: false,
        enableSortingRemoval: false,
    });

    React.useEffect(() => {
        if (typeof setSelectedColumns === "function" && typeof setSelectedIds === "function") {
            const selectedRows = table.getFilteredSelectedRowModel().rows;
            setSelectedColumns(selectedRows.length);
            setSelectedIds(
                selectedRows.map((row) => {
                    return row.original.id;
                })
            );
        }
    }, [rowSelection, setSelectedColumns, setSelectedIds, table]);

    React.useEffect(() => {
        if (typeof onSortChange === "function") {
            onSortChange(sorting);
        }
    }, [sorting, onSortChange]);

    React.useEffect(() => {
        if (typeof onPaginationChange === "function") {
            onPaginationChange(pagination);
        }
    }, [pagination, onPaginationChange]);


    return isLoaded ? (
        <div ref={scrollContainerRef} className={`scrollable-table-container ${isScrollable ? "scroll-table" : ""} ${isSelectable ? "list-container" : "list-container non-selectable"}`}>
            <table className={isFixed ? "table-fixed" : ""}>
                <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map((header) => (
                                <th
                                    key={header.id}
                                    className={header.column.columnDef.icon ? "column-icon " + header.column.columnDef.icon : "" + (header.id === "selection" ? "checkbox-fixed-size" : "")}
                                    onClick={header.column.getCanSort() ? header.column.getToggleSortingHandler() : undefined}
                                    style={{ cursor: header.column.getCanSort() ? "pointer" : "default" }}
                                >
                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                    {header.id !== "selection" &&
                                        header.column.getCanSort() &&
                                        (header.column.getIsSorted() ? (
                                            header.column.getIsSorted() === "desc" ? (
                                                <span className="sorted-desc" />
                                            ) : (
                                                <span className="sorted" />
                                            )
                                        ) : (
                                            <span className="sorted-inactive" />
                                        ))}
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                {items.length > 0 ? (
                    <tbody>
                        {table.getRowModel().rows.map((row) => {
                            return (
                                <tr
                                    key={row.id}
                                    onClick={() => {
                                        if (onRowCLick) {
                                            onRowCLick(row.original);
                                        }
                                    }}
                                    style={{
                                        cursor: onRowCLick ? 'pointer' : 'default'
                                    }}
                                >
                                    {row.getVisibleCells().map((cell) => {
                                        const baseLinkPath = intermediatePath ? `${intermediatePath}/` : "";
                                        let fullPath = baseLinkPath + (rowIdAccessor ? row.original[rowIdAccessor] : row.original.id);
                                        if (entityType) {
                                            fullPath = `/admin/inventory/${entityType}/${row.original.id}`;
                                        }
                                        return (
                                            <td key={cell.id}>
                                                {cell.column.id === "selection" ? (
                                                    flexRender(cell.column.columnDef.cell, cell.getContext())
                                                ) : isClickable ? (
                                                    <Link to={fullPath}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</Link>
                                                ) : (
                                                    flexRender(cell.column.columnDef.cell, cell.getContext())
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                ) : (
                    <tbody>
                        <tr>
                            <td colSpan={7}>
                                <div className="empty-list-container">
                                    <img src={emptyIcon} alt="icon" width={80} />
                                    <span>{emptyPlaceholder}</span>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                )}
            </table>
            {isPaginable && items.length > 0 ? (
                <div className="pagination-container">
                    <div className="current-page-info">
                        <span>
                            Page{" "}
                            <strong>
                                {table.getState().pagination.pageIndex + 1} sur {table.getPageCount()}
                            </strong>
                        </span>
                    </div>
                    <div className="page-switcher">
                        <button className="pagination-button" onClick={() => table.setPageIndex(0)} disabled={!table.getCanPreviousPage()}>
                            {"<<"}
                        </button>
                        <button className="pagination-button" onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>
                            {"<"}
                        </button>
                        <button className="pagination-button" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
                            {">"}
                        </button>
                        <button className="pagination-button" onClick={() => table.setPageIndex(table.getPageCount() - 1)} disabled={!table.getCanNextPage()}>
                            {">>"}
                        </button>
                    </div>
                    <div className="rows-by-page">
                        <span>Lignes par page</span>
                        <select
                            value={table.getState().pagination.pageSize}
                            className="pagination-button select"
                            onChange={(e) => {
                                table.setPageSize(Number(e.target.value));
                            }}
                        >
                            {" "}
                            {[20, 50, 100, 200].map((pageSize) => (
                                <option key={pageSize} value={pageSize}>
                                    {pageSize}
                                </option>
                            ))}
                        </select>
                        {isDownloadable ? <CustomDownloader columns={columns} data={items} smallButton /> : null}
                    </div>
                </div>
            ) : null}

            {!isPaginable && isDownloadable ? (
                <div className="pagination-container">
                    <CustomDownloader columns={columns} data={items} />
                </div>
            ) : null}
        </div>
    ) : (
        <div className="loader-container absolute">
            <CircularProgress size={30} />
        </div>
    );
};

export default CustomList;
